import type { SaveJobInput } from "@/lib/saved-jobs";

/**
 * Device-local bookmark for signed-out visitors — the same "don't make them
 * create an account before we've delivered anything" move as the alert nudge
 * (AlertNudge.tsx). Storage only, no consent gate needed (see lib/consent.ts):
 * a first-party UI preference, not analytics.
 *
 * Snapshots the job (not just its id) so a future guest-facing list can render
 * without a DB round trip — mirrors what saveJob() persists server-side.
 */

const KEY = "cm.savedjobs.v1"; // gitleaks:allow — localStorage key name, not a credential

export interface LocalSavedJob extends SaveJobInput {
  savedAt: string;
}

function readAll(): Record<number, LocalSavedJob> {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeAll(all: Record<number, LocalSavedJob>): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    // Private mode / storage disabled — the toggle still works for this
    // render, it just won't survive a reload.
  }
}

export function isLocallySaved(jobId: number): boolean {
  try {
    return jobId in readAll();
  } catch {
    return false;
  }
}

export function listLocallySaved(): LocalSavedJob[] {
  return Object.values(readAll()).sort((a, b) => b.savedAt.localeCompare(a.savedAt));
}

/** Toggle a job's local-save state; returns the resulting saved flag. */
export function toggleLocalSave(job: SaveJobInput): boolean {
  const all = readAll();
  if (job.jobId in all) {
    delete all[job.jobId];
    writeAll(all);
    return false;
  }
  all[job.jobId] = { ...job, savedAt: new Date().toISOString() };
  writeAll(all);
  return true;
}
